import type { ICellEditorParams } from "ag-grid-community";
import type { TColumnHeaderProps } from "../components";

// Requirements
// - Only toggle edit mode on number, dash or dot key's
// - Allow comma or dot as decimal separator

const startsWithNumbersRegex = /^[0-9.,-]$/;
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

/** Docs @link https://www.ag-grid.com/javascript-data-grid/cell-editors/ */
export class NumberCellEditor {
  input: HTMLInputElement | null = null;
  done: (() => void) | null = null;
  cancel = false;
  isPercent = false;

  // gets called once after the editor is created
  init(params: ICellEditorParams): void {
    this.done = params.stopEditing;

    // Check if this column has percent as UOM
    const headerParams = params.column?.getColDef()?.headerComponentParams as TColumnHeaderProps | undefined;
    this.isPercent = headerParams?.columnLabels?.uom === "percent";

    this.input = document.createElement("input");
    this.input.classList.add("focus:outline-none");
    this.input.style.textAlign = "right";
    this.input.style.width = "100%";
    this.input.style.maxWidth = "100%";
    this.input.setAttribute("type", "text");
    this.input.setAttribute("inputmode", isIOS ? "text" : "decimal");
    this.input.setAttribute("autocomplete", "off");
    this.input.setAttribute("autocorrect", "off");
    this.input.setAttribute("autocapitalize", "none");
    this.input.setAttribute("spellcheck", "false");
    this.input.setAttribute("aria-label", "Numeric input");
    this.input.setAttribute("aria-required", "true");

    /* -------------------------------------------------------------
     * Decide what should be inside the <input> when the editor opens
     * ------------------------------------------------------------- */
    const startKey = params.eventKey; // key that triggered edit (if any)
    const startValue = params.value; // current cell value

    // For percentage columns, multiply by 100 for display
    const displayValue =
      this.isPercent && startValue !== null && startValue !== undefined ? startValue * 100 : startValue;

    // Special case: if Backspace or Delete triggered the edit, start with empty input (Excel behavior)
    if (startKey === "Backspace" || startKey === "Delete") {
      this.input.value = "";
      this.cancel = false;
    }
    // 1) If the user started typing (0-9 . , -) we put that key into the input
    // 2) Otherwise we use the cell value (if it is not null / undefined)
    // 3) If both are "missing" → empty string
    else {
      this.input.value = startsWithNumbersRegex.test(startKey ?? "")
        ? (startKey ?? "")
        : displayValue !== null && displayValue !== undefined
          ? String(displayValue)
          : "";

      // We still honor the "cancel if first key is not numeric" rule
      this.cancel = startKey ? !startsWithNumbersRegex.test(startKey) : false;
    }

    this.input.addEventListener("input", (event: Event) => {
      const input = event.target as HTMLInputElement;
      const cursorPosition = input.selectionStart ?? 0;
      const oldValue = input.value;
      const newValue = oldValue.replace(/[^0-9.,-]/g, "");
      if (newValue !== oldValue) {
        input.value = newValue;
        const diff = oldValue.length - newValue.length;
        input.setSelectionRange(cursorPosition - diff, cursorPosition - diff);
      }
    });

    this.input.addEventListener("keydown", (event: KeyboardEvent) => params.onKeyDown(event));
  }

  // Gets called once after GUI is attached to DOM.
  // Useful if you want to focus or highlight a component
  // (this is not possible when the element is not attached)
  afterGuiAttached(): void {
    this.input?.focus();
  }

  // Mandatory - Return the DOM element of your editor, this is what the grid puts into the DOM
  getGui(): HTMLElement {
    return this.input as HTMLElement;
  }

  // Mandatory - Return the final value - called by the grid once after editing is complete
  getValue() {
    // 1. trim – user may have hit Space
    const raw = (this.input?.value ?? "").trim();

    // 2. Empty → keep the cell empty
    if (raw === "") {
      return null; // or `undefined` / '' – whatever your row-model uses
    }

    // 3. Convert comma to dot, then parse
    const num = Number.parseFloat(raw.replaceAll(",", "."));

    // 4. Valid number? use it, otherwise keep cell empty
    if (!Number.isFinite(num)) return null;

    // 5. For percentage columns, divide by 100 to store as decimal
    return this.isPercent ? num / 100 : num;
  }

  // Gets called once by grid after editing is finished
  // if your editor needs to do any cleanup, do it here
  destroy(): void {
    this.input?.remove();
    this.input = null;
  }

  // Gets called once after initialised. If you return true, the editor will
  // appear in a popup, so is not constrained to the boundaries of the cell.
  // This is great if you want to, for example, provide you own custom dropdown list
  // for selection. Default is false (ie if you don't provide the method).
  isPopup?(): boolean;

  // Gets called once, only if isPopup() returns true. Return "over" if the
  // popup should cover the cell, or "under" if it should be positioned below
  // leaving the cell value visible. If this method is not present, the
  // default is "over"
  getPopupPosition?(): string;

  // Gets called once after initialised. If you return true, the editor will not be
  // used and the grid will continue editing. Use this to make a decision on editing
  // inside the init() function, eg maybe you want to only start editing if the user
  // hits a numeric key, but not a letter, if the editor is for numbers.
  isCancelBeforeStart() {
    return this.cancel;
  }

  // Gets called once after editing is complete. If your return true, then the new
  // value will not be used. The editing will have no impact on the record. Use this
  // if you do not want a new value from your gui, i.e. you want to cancel the editing.
  isCancelAfterEnd(): boolean {
    const v = (this.input?.value ?? "").trim();

    if (v === "") return false; // blank is allowed

    // allow: optional leading '-', digits, optional decimal part (dot|comma)
    const NUMBER_RE = /^-?\d*(?:[.,]\d*)?$/;

    return !NUMBER_RE.test(v);
  }

  // If doing full line edit, then gets called when focus should be put into the editor
  focusIn?(): boolean;

  // If doing full line edit, then gets called when focus is leaving the editor
  focusOut?(): boolean;
}
